import { DatePicker, InputNumber, Select, TimePicker } from 'antd'
import classNames from 'classnames'
import type { FC } from 'react'

import { useTimezone } from 'common/hooks/useTimezone'
import { MAX_DURATION } from 'features/Series/series.constants'

import type { PersonOption } from '../../ProviderField'
import type { GridRow } from '../series.types'
import { APPT_STATUS_OPTIONS } from '../seriesDates'
import { fromPickerDate, fromPickerTime, toPickerDate, toPickerTime } from '../seriesPickers'
import { SERIES_TEXT_V2 } from '../seriesText'
import type { VisitPeople } from '../seriesPeople'
import { ProviderCell } from './ProviderCell'
import { RowWarning } from './RowWarning'
import styles from './seriesGrid.module.scss'

const DATE_FORMAT = 'M/D/YYYY'
const TIME_FORMAT = 'h:mm A'
const TIME_STEP = 15
const MENU_WIDTH = 280

export type GridEditPatch = {
  date?: string
  time?: string
  duration?: number
  people?: VisitPeople
  status?: string
}

// The known statuses, plus any code the site uses that we do not list.
const statusOptions = (
  codes: (string | null | undefined)[],
): { value: string; label: string }[] => {
  const extra = [...new Set(codes)].filter(
    (code): code is string =>
      typeof code === 'string' && !APPT_STATUS_OPTIONS.some((option) => option.value === code),
  )
  return [...APPT_STATUS_OPTIONS, ...extra.map((code) => ({ value: code, label: code }))]
}

type SeriesGridProps = {
  mode: 'create' | 'edit'
  rows: GridRow[]
  doctorOptions: PersonOption[]
  onEdit: (key: string, patch: GridEditPatch) => void
  onRemove?: (key: string) => void
}

const HEADERS = {
  create: [
    SERIES_TEXT_V2.colVisit,
    SERIES_TEXT_V2.colName,
    SERIES_TEXT_V2.colDate,
    SERIES_TEXT_V2.colTime,
    SERIES_TEXT_V2.colMin,
    SERIES_TEXT_V2.colProvider,
    SERIES_TEXT_V2.colInterval,
    '',
  ],
  edit: [
    SERIES_TEXT_V2.colVisit,
    SERIES_TEXT_V2.colName,
    SERIES_TEXT_V2.colDate,
    SERIES_TEXT_V2.colTime,
    SERIES_TEXT_V2.colMin,
    SERIES_TEXT_V2.colProvider,
    SERIES_TEXT_V2.colStatus,
    SERIES_TEXT_V2.colInterval,
    '',
  ],
} as const

const Chevron: FC = () => (
  <span className={styles.chev} aria-hidden='true'>
    ▾
  </span>
)

type RowProps = Omit<SeriesGridProps, 'rows'> & { row: GridRow; timeZone: string | undefined }

const GridRowView: FC<RowProps> = ({ mode, row, doctorOptions, onEdit, onRemove, timeZone }) => {
  const locked = !row.editable || Boolean(row.disabled)
  const label = (what: string) => `Visit ${row.visit} ${what}`
  const wrapClass = mode === 'create' ? styles.create : styles.edit

  return (
    <div className={styles.rowWrap}>
      <div
        className={classNames(styles.row, wrapClass, { [styles.opened]: row.opened })}
        data-testid={`series-row-${row.key}`}>
        <span className={styles.visit}>{row.visit}</span>
        <span className={styles.name}>{row.name}</span>
        <DatePicker
          aria-label={label('date')}
          className={classNames(styles.picker, { [styles.edited]: row.edited.date })}
          value={toPickerDate(row.date ?? '', timeZone)}
          format={DATE_FORMAT}
          disabled={locked}
          allowClear={false}
          suffixIcon={null}
          needConfirm={false}
          onChange={(value) => value && onEdit(row.key, { date: fromPickerDate(value, timeZone) })}
        />
        <TimePicker
          aria-label={label('time')}
          className={classNames(styles.picker, { [styles.edited]: row.edited.time })}
          value={toPickerTime(row.time ?? '')}
          format={TIME_FORMAT}
          use12Hours
          minuteStep={TIME_STEP}
          showNow={false}
          disabled={locked}
          allowClear={false}
          suffixIcon={null}
          needConfirm={false}
          onChange={(value) => value && onEdit(row.key, { time: fromPickerTime(value) })}
        />
        <InputNumber
          aria-label={label('duration in minutes')}
          className={styles.number}
          value={row.duration}
          min={1}
          max={MAX_DURATION}
          precision={0}
          controls={false}
          disabled={locked}
          onChange={(value) => value !== null && onEdit(row.key, { duration: value })}
        />
        <ProviderCell
          position={Number.parseInt(row.visit, 10)}
          options={doctorOptions}
          value={row.people}
          unknown={row.unknownPeople}
          readOnly={row.peopleReadOnly || row.doctorText !== undefined}
          lockedId={row.lockedPersonId}
          disabled={locked}
          text={row.doctorText}
          tone={row.refused || row.doctorTone === 'refused' ? 'refused' : 'normal'}
          onChange={(next) => onEdit(row.key, { people: next })}
        />
        {mode === 'edit' &&
          (row.statusCode !== undefined ? (
            <Select<string>
              aria-label={label('status')}
              className={classNames(styles.doctor, styles.statusSelect)}
              value={row.statusCode ?? undefined}
              placeholder={SERIES_TEXT_V2.dash}
              disabled={locked}
              virtual={false}
              showSearch={false}
              suffixIcon={<Chevron />}
              popupMatchSelectWidth={MENU_WIDTH}
              classNames={{ popup: { root: styles.menu } }}
              options={statusOptions([row.statusCode, row.savedStatusCode])}
              onChange={(next) => onEdit(row.key, { status: next })}
            />
          ) : (
            <div className={styles.box}>
              <span className={styles.boxText}>{row.status}</span>
              <Chevron />
            </div>
          ))}
        <span className={styles.interval}>{row.interval}</span>
        {mode === 'edit' && <span aria-hidden='true' />}
        {mode === 'create' && (
          <span>
            {row.removable && onRemove && (
              <button
                type='button'
                className={styles.remove}
                aria-label={`${SERIES_TEXT_V2.removeVisit} ${row.visit}`}
                onClick={() => onRemove(row.key)}>
                <svg
                  width='12'
                  height='12'
                  viewBox='0 0 24 24'
                  fill='none'
                  stroke='currentColor'
                  strokeWidth='2.4'
                  strokeLinecap='round'
                  aria-hidden='true'>
                  <path d='M18 6 6 18' />
                  <path d='m6 6 12 12' />
                </svg>
              </button>
            )}
          </span>
        )}
      </div>
      <RowWarning messages={row.warnings} refused={row.refused} />
    </div>
  )
}

export const SeriesGrid: FC<SeriesGridProps> = ({ rows, ...rest }) => {
  const { timeZone } = useTimezone()
  const wrapClass = rest.mode === 'create' ? styles.create : styles.edit
  return (
    <div className={styles.grid}>
      <div className={classNames(styles.head, wrapClass)}>
        {HEADERS[rest.mode].map((title, index) => (
          <span key={`${title}-${index}`}>{title}</span>
        ))}
      </div>
      {rows.map((row) => (
        <GridRowView key={row.key} row={row} timeZone={timeZone} {...rest} />
      ))}
    </div>
  )
}
